/**
 * VendorDashboard — the workflow small vendors need:
 *   1. Search the FoodPulse master catalog for items they carry
 *   2. Add to today's inventory (qty, price, unit)
 *   3. Promote any row to a live deal in one click (with expiry, discount, pickup)
 *   4. See own deals with countdown, republish, or archive
 *
 * Route: /food-pulse/vendor
 * Auth: current_user required (falls back to a login prompt).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Package, Plus, Search, Trash2, Sparkles, Tag, Clock, RefreshCw,
  DollarSign, X, Loader2, Store, TrendingUp, Camera, ShieldCheck, Award, Sprout, ExternalLink, Upload,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

function fmtCountdown(endsAt) {
  if (!endsAt) return "no expiry";
  const diff = new Date(endsAt).getTime() - Date.now();
  if (diff <= 0) return "expired";
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// -------------------------------------------------------------------
// Catalog picker — search the master food_items and add to inventory
// -------------------------------------------------------------------
function CatalogPicker({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/api/food-items`, { params: { q: q || undefined, limit: 24 } })
      .then((r) => setItems(r.data.items || r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16 px-4" onClick={onClose} data-testid="catalog-picker">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[80vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: "#E9D8B4" }}>
          <div className="flex-1 flex items-center gap-2 rounded-full border px-4 py-2" style={{ borderColor: "#E9D8B4" }}>
            <Search className="w-4 h-4 opacity-50" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search food items…"
                   className="flex-1 outline-none text-sm" data-testid="catalog-search" />
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-black/5" data-testid="catalog-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin opacity-40" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 opacity-50 text-sm">No items match. Try another search.</div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              {items.map((it) => (
                <button
                  key={it.slug}
                  onClick={() => onPick(it)}
                  className="text-left rounded-2xl overflow-hidden border hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  style={{ borderColor: "#E9D8B4", background: "#fff" }}
                  data-testid={`catalog-pick-${it.slug}`}
                >
                  <div className="aspect-square overflow-hidden" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
                    {(it.hero_image || it.image_url) && <img src={it.hero_image || it.image_url} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-3">
                    <div className="font-display text-base line-clamp-1">{it.name || it.slug}</div>
                    {it.category && <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{it.category}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Promote-to-deal dialog
// -------------------------------------------------------------------
function PromoteDialog({ open, row, onClose, onDone }) {
  const [discount, setDiscount] = useState(30);
  const [hours, setHours] = useState(6);
  const [pickup, setPickup] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDiscount(30); setHours(6); setPickup(""); } }, [open]);
  if (!open || !row) return null;

  const submit = async () => {
    setBusy(true);
    const endsAt = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    try {
      const r = await http.post(`/vendor/inventory/${row.id}/promote-to-deal`, {
        kind: "surplus", discount_pct: Number(discount), ends_at: endsAt, pickup_location: pickup || undefined,
      });
      onDone(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not promote.");
    } finally { setBusy(false); }
  };

  const deal_price = row.price != null && discount ? (row.price * (1 - discount / 100)).toFixed(2) : row.price;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={onClose} data-testid="promote-dialog">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: "#E9D8B4" }}>
          <Sparkles className="w-5 h-5" style={{ color: AMBER }} />
          <div className="font-display text-xl">Publish deal — {row.food_item_name}</div>
          <button onClick={onClose} className="ml-auto rounded-full p-1 hover:bg-black/5"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest opacity-70">Discount %</label>
            <input type="number" min={0} max={90} value={discount} onChange={(e) => setDiscount(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="promote-discount" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest opacity-70">Expires in (hours)</label>
            <input type="number" min={1} max={72} value={hours} onChange={(e) => setHours(e.target.value)}
                   className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="promote-hours" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest opacity-70">Pickup instructions (optional)</label>
            <input value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="e.g. Front counter until 6pm"
                   className="mt-1 w-full rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}
                   data-testid="promote-pickup" />
          </div>
          {row.price != null && (
            <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "#FFF7E4" }}>
              Deal price will be <span className="font-semibold">${deal_price}</span>
              <span className="opacity-60"> (from ${row.price}/{row.unit})</span>
            </div>
          )}
          <button onClick={submit} disabled={busy}
                  className="w-full rounded-full py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: GREEN }}
                  data-testid="promote-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
            Publish deal live
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Main dashboard
// -------------------------------------------------------------------
export default function VendorDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("inventory");
  const [inventory, setInventory] = useState([]);
  const [deals, setDeals] = useState([]);
  const [scoutInbox, setScoutInbox] = useState([]);
  const [myFarms, setMyFarms] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [promoteRow, setPromoteRow] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const [inv, mine, inbox, myFarms] = await Promise.all([
        http.get(`/vendor/inventory`),
        http.get(`/deals/mine`),
        http.get(`/vendor/scout-inbox`).catch(() => ({ data: { items: [] } })),
        http.get(`/farms/my`).catch(() => ({ data: { items: [] } })),
      ]);
      setInventory(inv.data.items || []);
      setDeals(mine.data.items || []);
      setScoutInbox(inbox.data.items || []);
      setMyFarms(myFarms.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const addFromCatalog = async (item) => {
    setPickerOpen(false);
    try {
      const r = await http.post(`/vendor/inventory`, {
        food_item_slug: item.slug,
        food_item_name: item.name,
        food_item_image: item.hero_image || item.image_url,
        category: item.category,
        quantity: 1, unit: "each",
      });
      setInventory((prev) => [r.data, ...prev.filter((x) => x.id !== r.data.id)]);
    } catch (e) { alert(e?.response?.data?.detail || "Add failed"); }
  };

  const updateRow = async (id, patch) => {
    try {
      const r = await http.patch(`/vendor/inventory/${id}`, patch);
      setInventory((prev) => prev.map((x) => x.id === id ? r.data : x));
    } catch (e) { alert(e?.response?.data?.detail || "Update failed"); }
  };
  const deleteRow = async (id) => {
    if (!confirm("Remove this row?")) return;
    try {
      await http.delete(`/vendor/inventory/${id}`);
      setInventory((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  const republish = async (slug) => {
    const hours = prompt("Republish for how many hours?", "6");
    if (!hours) return;
    const endsAt = new Date(Date.now() + Number(hours) * 3600 * 1000).toISOString();
    try {
      const r = await http.post(`/deals/${slug}/republish`, { ends_at: endsAt });
      setDeals((prev) => [r.data, ...prev]);
      alert(`Republished as ${r.data.slug}`);
    } catch (e) { alert(e?.response?.data?.detail || "Republish failed"); }
  };
  const archiveDeal = async (slug) => {
    if (!confirm("Archive this deal? Shoppers can no longer see it.")) return;
    try {
      await http.delete(`/deals/${slug}`);
      setDeals((prev) => prev.filter((d) => d.slug !== slug));
    } catch (e) { alert(e?.response?.data?.detail || "Archive failed"); }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Store className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-3">Vendor dashboard</h1>
          <p className="opacity-80 mb-6">Sign in with the account you claimed your business on. Then post your first surplus deal in under a minute.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }} data-testid="vendor-login-cta">
            Sign in to continue
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="vendor-dashboard">
      <SeoHead section="food-pulse-vendor" title="Vendor Dashboard — Food Pulse" description="Manage inventory and post live deals." />

      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Vendor</div>
            <h1 className="font-display text-4xl">Your daily storefront</h1>
            <p className="opacity-70 text-sm mt-1">Pick items you have today. Set qty and price. Publish deals in one click.</p>
          </div>
          <button onClick={reload} className="rounded-full p-2 hover:bg-black/5" title="Reload" data-testid="vendor-refresh">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { k: "inventory", l: `Inventory (${inventory.length})`, icon: Package },
            { k: "deals",     l: `Live deals (${deals.filter(d => !d.is_expired).length})`, icon: Tag },
            { k: "scout",     l: `Scout inbox (${scoutInbox.filter(s => s.claim_status === "unclaimed").length})`, icon: Camera, highlight: scoutInbox.filter(s => s.claim_status === "unclaimed").length > 0 },
            { k: "leaderboard", l: "Leaderboard", icon: Award },
            { k: "farm",      l: `Farm profile${myFarms.length > 0 ? ` (${myFarms.length})` : ""}`, icon: Sprout },
            { k: "history",   l: `History (${deals.filter(d => d.is_expired).length})`, icon: TrendingUp },
          ].map(({ k, l, icon: Ico, highlight }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${tab === k ? "text-white" : "opacity-70 hover:opacity-100"} ${highlight && tab !== k ? "ring-2 ring-amber-300" : ""}`}
              style={{ background: tab === k ? GREEN : "transparent" }}
              data-testid={`vendor-tab-${k}`}
            >
              <Ico className="w-4 h-4" /> {l}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Link to="/food-pulse/vendor/import"
                  className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:shadow-sm transition-shadow"
                  style={{ borderColor: GREEN, color: GREEN, background: "#fff" }}
                  data-testid="vendor-import-csv-btn">
              <Upload className="w-4 h-4" /> Import CSV
            </Link>
            <button onClick={() => setPickerOpen(true)}
                    className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
                    style={{ background: AMBER, color: GREEN }}
                    data-testid="vendor-add-item">
              <Plus className="w-4 h-4" /> Add item from catalog
            </button>
          </div>
        </div>

        {loading && <div className="text-center py-16 opacity-50">Loading your storefront…</div>}

        {/* Inventory tab */}
        {!loading && tab === "inventory" && (
          <div>
            {inventory.length === 0 ? (
              <div className="rounded-3xl border border-dashed p-14 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
                <Package className="w-14 h-14 mx-auto opacity-40 mb-3" />
                <div className="font-display text-2xl mb-2">Your storefront is empty</div>
                <div className="opacity-70 text-sm max-w-md mx-auto mb-4">
                  Add the first item you have today. It only takes 5 seconds — pick a food item, set the quantity and price.
                </div>
                <button onClick={() => setPickerOpen(true)} className="rounded-full px-6 py-3 text-white font-semibold" style={{ background: GREEN }}
                        data-testid="vendor-empty-add">
                  <Plus className="w-4 h-4 inline mr-1" /> Add your first item
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {inventory.map((row) => (
                  <div key={row.id} className="rounded-2xl border p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center" style={{ borderColor: "#E9D8B4", background: "#fff" }}
                       data-testid={`inventory-row-${row.id}`}>
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
                      {row.food_item_image && <img src={row.food_item_image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg">{row.food_item_name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">{row.category || "food"}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                        <label className="text-xs">
                          <span className="opacity-60">Qty</span>
                          <input type="number" min={0} defaultValue={row.quantity}
                                 onBlur={(e) => Number(e.target.value) !== row.quantity && updateRow(row.id, { quantity: Number(e.target.value) })}
                                 className="mt-0.5 w-full rounded-md border px-2 py-1" style={{ borderColor: "#E9D8B4" }}
                                 data-testid={`inventory-qty-${row.id}`} />
                        </label>
                        <label className="text-xs">
                          <span className="opacity-60">Unit</span>
                          <input defaultValue={row.unit}
                                 onBlur={(e) => e.target.value !== row.unit && updateRow(row.id, { unit: e.target.value })}
                                 className="mt-0.5 w-full rounded-md border px-2 py-1" style={{ borderColor: "#E9D8B4" }} />
                        </label>
                        <label className="text-xs">
                          <span className="opacity-60">Price</span>
                          <input type="number" step="0.01" min={0} defaultValue={row.price || ""}
                                 onBlur={(e) => Number(e.target.value) !== row.price && updateRow(row.id, { price: Number(e.target.value) })}
                                 className="mt-0.5 w-full rounded-md border px-2 py-1" style={{ borderColor: "#E9D8B4" }} />
                        </label>
                        <label className="text-xs">
                          <span className="opacity-60">Original $</span>
                          <input type="number" step="0.01" min={0} defaultValue={row.original_price || ""}
                                 onBlur={(e) => Number(e.target.value) !== row.original_price && updateRow(row.id, { original_price: Number(e.target.value) })}
                                 className="mt-0.5 w-full rounded-md border px-2 py-1" style={{ borderColor: "#E9D8B4" }} />
                        </label>
                      </div>
                      <div className="mt-2 text-[11px] opacity-60">
                        Status: {row.status}
                        {row.active_deal_slug && <> · <Link to={`/food-pulse/deals/${row.active_deal_slug}`} className="underline">deal live · ends {fmtCountdown(row.active_deal_ends_at)}</Link></>}
                      </div>
                    </div>
                    <div className="flex sm:flex-col gap-2">
                      <button onClick={() => setPromoteRow(row)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white flex items-center gap-1" style={{ background: AMBER, color: GREEN }} data-testid={`inventory-promote-${row.id}`}>
                        <Sparkles className="w-3.5 h-3.5" /> Publish deal
                      </button>
                      <button onClick={() => deleteRow(row.id)} className="rounded-full px-3 py-1.5 text-xs text-red-700 flex items-center gap-1 border border-red-200" data-testid={`inventory-delete-${row.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Live Deals tab */}
        {!loading && tab === "deals" && (
          <DealsList
            deals={deals.filter(d => !d.is_expired)}
            emptyLabel="No live deals right now."
            onRepublish={republish}
            onArchive={archiveDeal}
          />
        )}
        {/* Scout Inbox tab — community deals matched to my vendor(s) */}
        {!loading && tab === "scout" && (
          <ScoutInbox items={scoutInbox} onClaim={async (slug) => {
            try {
              await http.post(`/deals/${slug}/claim`);
              reload();
              alert("Deal claimed — now yours to modify.");
            } catch (e) { alert(e?.response?.data?.detail || "Claim failed"); }
          }} />
        )}

        {/* Leaderboard tab */}
        {!loading && tab === "leaderboard" && (
          <Leaderboard scoutInbox={scoutInbox} />
        )}

        {/* Farm profile tab — lets owners jump to the FarmProfileEditor */}
        {!loading && tab === "farm" && (
          <div data-testid="vendor-farm-tab">
            {myFarms.length === 0 ? (
              <div className="rounded-3xl border border-dashed p-14 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
                <Sprout className="w-14 h-14 mx-auto opacity-40 mb-3" />
                <div className="font-display text-2xl mb-2">Claim a business first</div>
                <div className="opacity-70 text-sm max-w-md mx-auto mb-4">
                  Farm profiles are unlocked after you verify ownership of a listing.
                  Head to your business page and tap <b>Claim this listing</b>.
                </div>
                <Link to="/food-pulse/businesses" className="rounded-full px-6 py-3 text-white font-semibold inline-block" style={{ background: GREEN }} data-testid="farm-tab-cta-claim">
                  Find your business →
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {myFarms.map((f) => (
                  <div key={f.slug} className="rounded-2xl border bg-white p-5 flex flex-col gap-3"
                       style={{ borderColor: "#E9D8B4" }}
                       data-testid={`farm-tab-card-${f.slug}`}>
                    <div className="flex items-start gap-3">
                      {f.hero_image ? (
                        <img src={f.hero_image} alt={f.name} className="w-16 h-16 rounded-lg object-cover border" style={{ borderColor: "#E9D8B4" }} />
                      ) : (
                        <div className="w-16 h-16 rounded-lg flex items-center justify-center border" style={{ borderColor: "#E9D8B4", background: "#F0F5F1" }}>
                          <Sprout className="w-6 h-6" style={{ color: GREEN }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg truncate">{f.name}</div>
                        <div className="text-xs opacity-70">{[f.city, f.state_code || f.state].filter(Boolean).join(", ")}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "#F0E2C7", color: "#8B5E10" }}>
                            {(f.business_type || "business").replace(/_/g, " ")}
                          </span>
                          {f.is_farm && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "#DCEAD1", color: "#2F6B2F" }}>
                              Farm profile ✓
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Link to={`/food-pulse/farm/edit/${f.slug}`}
                            className="rounded-full px-4 py-2 text-xs font-semibold text-white flex items-center gap-1"
                            style={{ background: GREEN }}
                            data-testid={`farm-tab-edit-${f.slug}`}>
                        <Sprout className="w-3.5 h-3.5" /> {f.is_farm ? "Edit farm profile" : "Set up as farm"}
                      </Link>
                      <Link to={`/food-pulse/farm/${f.slug}`} target="_blank"
                            className="rounded-full px-4 py-2 text-xs font-semibold border flex items-center gap-1"
                            style={{ borderColor: GREEN, color: GREEN }}
                            data-testid={`farm-tab-view-${f.slug}`}>
                        View farmstand <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History tab */}
        {!loading && tab === "history" && (
          <DealsList
            deals={deals.filter(d => d.is_expired)}
            emptyLabel="No past deals yet."
            onRepublish={republish}
            onArchive={archiveDeal}
            expired
          />
        )}
      </div>

      <CatalogPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={addFromCatalog} />
      <PromoteDialog open={!!promoteRow} row={promoteRow} onClose={() => setPromoteRow(null)}
        onDone={(deal) => { setPromoteRow(null); setDeals((prev) => [deal, ...prev]); reload(); }} />
    </div>
  );
}

function DealsList({ deals, emptyLabel, onRepublish, onArchive, expired }) {
  if (deals.length === 0) return <div className="text-center py-12 opacity-60 text-sm">{emptyLabel}</div>;
  return (
    <div className="grid gap-3">
      {deals.map((d) => (
        <div key={d.slug} className="rounded-2xl border p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
             style={{ borderColor: "#E9D8B4", background: "#fff" }} data-testid={`vendor-deal-row-${d.slug}`}>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
            {d.image_urls?.[0] && <img src={d.image_urls[0]} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg truncate">{d.title}</div>
            <div className="text-[11px] opacity-60">
              {d.deal_price != null && <><DollarSign className="w-3 h-3 inline" />{d.deal_price}{d.unit ? `/${d.unit}` : ""} · </>}
              <Clock className="w-3 h-3 inline" /> {expired ? "expired" : `${fmtCountdown(d.ends_at)} left`} ·
              {d.publish_count > 1 && <span className="ml-1 rounded-full px-1.5 py-0.5" style={{ background: "#FFF7E4" }}>v{d.publish_count}</span>}
              <span className="ml-1">{d.view_count || 0} views · {d.message_count || 0} messages</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/food-pulse/deals/${d.slug}`} className="rounded-full px-3 py-1.5 text-xs border" style={{ borderColor: GREEN, color: GREEN }}>View</Link>
            <button onClick={() => onRepublish(d.slug)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: AMBER, color: GREEN }} data-testid={`republish-${d.slug}`}>
              Republish
            </button>
            {!expired && (
              <button onClick={() => onArchive(d.slug)} className="rounded-full px-3 py-1.5 text-xs text-red-700 border border-red-200" data-testid={`archive-${d.slug}`}>Archive</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoutInbox({ items, onClaim }) {
  if (items.length === 0) return (
    <div className="rounded-3xl border border-dashed p-14 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }} data-testid="scout-inbox-empty">
      <Camera className="w-14 h-14 mx-auto opacity-40 mb-3" />
      <div className="font-display text-2xl mb-2">No community deals yet</div>
      <div className="opacity-70 text-sm max-w-md mx-auto">
        Once shoppers spot your sales, they'll show up here — you can claim, tweak, and republish them.
      </div>
    </div>
  );
  return (
    <div className="grid gap-3">
      {items.map((d) => (
        <div key={d.slug} className="rounded-2xl border p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
             style={{ borderColor: d.claim_status === "unclaimed" ? AMBER : "#E9D8B4", background: "#fff" }}
             data-testid={`scout-inbox-row-${d.slug}`}>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
            {d.image_urls?.[0] && <img src={d.image_urls[0]} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg truncate">{d.title}</div>
            <div className="text-[11px] opacity-70 flex gap-2 flex-wrap items-center">
              <span><Camera className="w-3 h-3 inline mr-0.5" />{d.scout_name}</span>
              {d.deal_price != null && <span>· ${d.deal_price}{d.unit ? `/${d.unit}` : ""}</span>}
              <span>· <Clock className="w-3 h-3 inline" /> {d.is_expired ? "expired" : `${fmtCountdown(d.ends_at)} left`}</span>
              <span>· {d.share_count || 0} shares · verify {d.verification_score >= 0 ? `+${d.verification_score}` : d.verification_score}</span>
              {d.claim_status === "unclaimed" && <span className="rounded-full px-1.5 py-0.5" style={{ background: AMBER, color: GREEN }}>NEW · unclaimed</span>}
              {d.claim_status === "claimed" && <span className="rounded-full px-1.5 py-0.5" style={{ background: "#F0F5F1", color: GREEN }}><ShieldCheck className="w-3 h-3 inline" /> claimed</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/food-pulse/deals/${d.slug}`} className="rounded-full px-3 py-1.5 text-xs border" style={{ borderColor: GREEN, color: GREEN }}>View</Link>
            {d.claim_status === "unclaimed" && (
              <button onClick={() => onClaim(d.slug)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: GREEN }} data-testid={`claim-${d.slug}`}>
                Claim this deal
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function Leaderboard({ scoutInbox }) {
  // Aggregate scouts client-side from inbox items (server also exposes /vendor/scout-leaderboard per-vendor)
  const map = {};
  for (const d of scoutInbox) {
    if (!d.scout_id) continue;
    const s = (map[d.scout_id] ||= { scout_id: d.scout_id, scout_name: d.scout_name, submitted: 0, claimed: 0, shares: 0, views: 0 });
    s.submitted++;
    if (d.claim_status === "claimed") s.claimed++;
    s.shares += d.share_count || 0;
    s.views  += d.view_count  || 0;
  }
  const rows = Object.values(map).sort((a, b) => b.submitted - a.submitted).slice(0, 20);

  if (rows.length === 0) return (
    <div className="rounded-3xl border border-dashed p-14 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }} data-testid="leaderboard-empty">
      <Award className="w-14 h-14 mx-auto opacity-40 mb-3" />
      <div className="font-display text-2xl mb-2">No scouts yet</div>
      <div className="opacity-70 text-sm max-w-md mx-auto">
        As shoppers spot deals at your store, you'll see who your top scouts are — a great group to reward with free items, coupons, or a shout-out.
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-4 rounded-2xl p-4 flex items-start gap-3" style={{ background: "#FFF7E4", border: "1px solid #E9D8B4" }}>
        <Award className="w-5 h-5" style={{ color: AMBER }} />
        <div className="text-sm">
          <strong>Reward your top scouts</strong> — offer them a coupon, discount, or free item.
          You get repeat sales, they get a thank-you, everyone wins.
          <span className="opacity-70"> (Reward tracking + coupon codes coming in v2.)</span>
        </div>
      </div>
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: CREAM, color: GREEN }}>
              <th className="text-left p-3 text-[11px] font-mono uppercase tracking-widest">Scout</th>
              <th className="text-right p-3 text-[11px] font-mono uppercase tracking-widest">Deals</th>
              <th className="text-right p-3 text-[11px] font-mono uppercase tracking-widest">Claimed</th>
              <th className="text-right p-3 text-[11px] font-mono uppercase tracking-widest">Shares</th>
              <th className="text-right p-3 text-[11px] font-mono uppercase tracking-widest">Views</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.scout_id} className="border-t" style={{ borderColor: "#E9D8B4" }} data-testid={`leaderboard-row-${r.scout_id}`}>
                <td className="p-3">
                  <span className="font-semibold">{i + 1}. {r.scout_name}</span>
                </td>
                <td className="p-3 text-right">{r.submitted}</td>
                <td className="p-3 text-right">{r.claimed}</td>
                <td className="p-3 text-right">{r.shares}</td>
                <td className="p-3 text-right">{r.views}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

